import _ from 'lodash';

import type { Approval } from '../../repositories/approvalRepository';
import type { User } from '../../zustandStore/slices';

export const getCurrentCombinedLists = ({ initialCombinedApprovals }: { initialCombinedApprovals: Approval[] }) => {
	return initialCombinedApprovals.toSorted((a, b) => a.assignedOn - b.assignedOn).toReversed();
};

type ApprovalsGroupName = 'pending' | 'approved' | 'declined' | 'canceled';
export type GroupedApprovals = Record<ApprovalsGroupName, { approvals: Approval[]; count: number }>;

export const groupApprovals = (approvals: Approval[], isActivityPage: boolean) => {
	return approvals.reduce<GroupedApprovals>(
		(acc, approval) => {
			const theApprovalStatus = getApprovalStatus(approval) || '';
			const isPendingRelated = isActivityPage
				? theApprovalStatus.includes('pending') && theApprovalStatus !== 'forwarded-pending'
				: theApprovalStatus.includes('pending');
			const isCanceledRelated = theApprovalStatus.includes('canceled');
			// we want to group all pending or canceled approvals in one group
			const groupKey = isPendingRelated ? 'pending' : isCanceledRelated ? 'canceled' : theApprovalStatus;

			if (!acc[groupKey]) {
				acc[groupKey] = { approvals: [], count: 0 };
			}

			const approvalStatusGroup = acc[groupKey];
			if (approvalStatusGroup) {
				let updatedApproval = { ...approval };

				switch (groupKey) {
					case 'pending': {
						updatedApproval = {
							...approval,
							isPending: true,
							isApproved: false,
							isDeclined: false,
							isCancelled: false,
						};
						break;
					}
					case 'approved': {
						updatedApproval = {
							...approval,
							isPending: false,
							isApproved: true,
							isDeclined: false,
							isCancelled: false,
						};
						break;
					}
					case 'canceled': {
						updatedApproval = {
							...approval,
							isPending: false,
							isApproved: false,
							isDeclined: false,
							isCancelled: true,
						};
						break;
					}
					case 'declined': {
						updatedApproval = {
							...approval,
							isPending: false,
							isApproved: false,
							isDeclined: true,
							isCancelled: false,
						};
						break;
					}
					default: {
						break;
					}
				}
				approvalStatusGroup.approvals.push(updatedApproval);
				approvalStatusGroup.count++;
			}

			return acc;
		},
		{
			pending: { approvals: [], count: 0 },
			approved: { approvals: [], count: 0 },
			declined: { approvals: [], count: 0 },
			canceled: { approvals: [], count: 0 },
		},
	);
};

type UpdateApprovalsType = {
	currentCombinedLists: Approval[];
	isUserCompanyOrSupportAdmin: boolean;
	teamId: string;
	user: User | undefined;
	isActivityPage: boolean;
};

export const updateApprovalItems = ({
	currentCombinedLists,
	isUserCompanyOrSupportAdmin,
	teamId,
	isActivityPage,
	user,
}: UpdateApprovalsType) => {
	// company admin can see all approvals in dashboard
	// team admin can see all teamapprovals in dashboard, but only approvals that are assigned to them and to him
	// in the approvals pages
	// manager can only seat approvals that are assigned to them in the approvals page
	const approvalsPageCollection = isUserCompanyOrSupportAdmin
		? currentCombinedLists
		: isActivityPage
			? currentCombinedLists.filter(approval => approval.request.teamId === teamId)
			: currentCombinedLists
					.filter(approval => approval.request.teamId === teamId)
					.filter(approval => (user?.userId ? _.includes(approval.assignees, user.userId) : false));

	const approvalsDashBoardCollection = isUserCompanyOrSupportAdmin
		? currentCombinedLists
		: currentCombinedLists.filter(approval => approval.request.teamId === teamId);

	return {
		approvalsPageCollection: _.chain(approvalsPageCollection)
			.uniqBy(function (approvalItem) {
				return approvalItem.subjectId;
			})
			.sortBy(function (approvalItem) {
				return approvalItem.assignedOn;
			})
			// eslint-disable-next-line unicorn/no-array-reverse
			.reverse()
			.value(),

		approvalsDashBoardCollection: _.chain(approvalsDashBoardCollection)
			.uniqBy(function (approvalItem) {
				return approvalItem.subjectId;
			})
			.sortBy(function (approvalItem) {
				return approvalItem.assignedOn;
			})
			// eslint-disable-next-line unicorn/no-array-reverse
			.reverse()
			.value(),
	};
};

// approvalStatusLookup is a mapping of: request state + approval outcome -> approval status
const approvalStatusLookup = new Map([
	['APPROVED,APPROVED', 'approved'],
	['APPROVED,CARD-CANCELED', 'card-canceled'],
	['APPROVED,REASSIGNED', 'approved'],
	['APPROVED,', 'pending'],
	['REJECTED,REASSIGNED', 'declined'],
	['REJECTED,REJECTED', 'declined'],
	['REJECTED,', 'pending'],
	['SUBMITTED,MANUAL_REASSIGN', 'forwarded-pending'],
	['SUBMITTED,REASSIGNED', 'forwarded-pending'],
	['SUBMITTED,SELF-REASSIGNED', 'forwarded-pending'],
	['SUBMITTED,', 'pending'],
]);

export const getApprovalStatus = (approval: Approval) => {
	const requestState = approval.request.state;
	const approvalOutcome = approval.outcome;
	let approvalStatus = approvalStatusLookup.get([requestState, approvalOutcome].join(','));
	if (approvalStatus === 'pending' && approval.reassignedByUser != null) {
		approvalStatus = 'pending-forwarded';
	}

	return approvalStatus || '';
};

export function getApprovalsCountGroups(
	groupedApprovals: Record<string, { approvals: Approval[]; count: number }>,
	approvalStates: readonly ['pending', 'approved', 'declined', 'canceled'],
) {
	return Object.keys(groupedApprovals).reduce(
		(acc, key) => {
			approvalStates.forEach(state => {
				if (key.includes(state)) {
					const group = groupedApprovals[key];
					if (group) {
						acc[state] += group.count;
					}
				}
			});
			return acc;
		},
		{
			pending: 0,
			approved: 0,
			declined: 0,
			canceled: 0,
		} as Record<(typeof approvalStates)[number], number>,
	);
}

export const isUserAssignedInTheAproval = (userId: User['userId'], approval: Approval) => {
	return approval.assignees.includes(userId);
};
